'use client'

import { memo, useEffect, useRef } from 'react'

export interface CodeSnippetProps extends React.HTMLAttributes<HTMLDivElement> {
  html?: string | null
}

const CodeSnippet = ({ html, ...props }: CodeSnippetProps) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = ref.current
    if (!container) return
    // Clean prev content
    container.innerHTML = html || ''

    // Excecute scripts finded in the HTML, once the browser is idle so
    // third-party embeds don't compete with the initial page render
    const executeScripts = () => {
      const scripts = Array.from(container.querySelectorAll('script'))
      scripts.forEach((oldScript) => {
        const newScript = document.createElement('script')
        // Copy attributes and content from the old script
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value)
        })
        newScript.text = oldScript.text
        oldScript.parentNode?.replaceChild(newScript, oldScript)
      })
    }

    const idleId =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(executeScripts, { timeout: 2000 })
        : window.setTimeout(executeScripts, 200)

    // Cleanup
    return () => {
      container.innerHTML = ''
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId as number)
      } else {
        window.clearTimeout(idleId as number)
      }
    }
  }, [html])

  return <div ref={ref} {...props} />
}
CodeSnippet.displayName = 'CodeSnippet'

export default memo(CodeSnippet)
